import React from 'react';
import { Headline } from '@sato/components';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { realEstatesApi } from '@api/apartments.api';
import { LinkContextType } from '@models/analyticsEvents.types';
import { apartmentStore } from '@stores/apartmentStore';
import { localizationStore, t } from '@stores/localization';

import { ApartmentCardSkeleton } from '../ApartmentCardSkeleton';
import { ApartmentProductCard } from '../ApartmentProductCard';

type Props = {
  /**
   * - near: Shows apartments in the current building and in the vicinity
   * - far: Shows apartments elsewhere than the current building
   */
  recommendationsDistance: 'near' | 'far';
};

const recommendationsAnalyticsLinkContextMap = {
  far: 'omasato-apartment-recommendations-farther',
  near: 'omasato-apartment-recommendations-near',
} satisfies Record<Props['recommendationsDistance'], LinkContextType>;

/** Shows apartment recommendations for the user in the form of apartment cards. */
export const ApartmentRecommendations = observer(
  ({ recommendationsDistance }: Props) => {
    const productId = apartmentStore.selectedApartment?.apartment?.productId;

    const {
      data: recommendedApartments,
      isLoading,
      isSuccess,
      isError,
    } = useQuery({
      queryKey: [
        'apartment-recommendations',
        productId,
        recommendationsDistance,
      ],
      queryFn: () => {
        if (!productId) throw Error('apartmentProductId is required');

        switch (recommendationsDistance) {
          case 'near': {
            return realEstatesApi.getApartmentRecommendationsNear(
              productId,
              localizationStore.language
            );
          }
          case 'far': {
            return realEstatesApi.getApartmentRecommendationsFar(
              productId,
              localizationStore.language
            );
          }
        }
      },
      select: (data) => data.data.content,
      enabled: !!productId,
    });

    // Early exit in case of an error or if there are not recommendations to show
    if (
      !productId ||
      isError ||
      (isSuccess && recommendedApartments.length === 0)
    ) {
      return null;
    }

    const renderContent = () => {
      if (isLoading) {
        return [...new Array(3).keys()].map((_, index) => (
          <ApartmentCardSkeleton
            className="shrink-0 grow-0 pr-3 sm:basis-1/2 lg:basis-1/3 smDown:basis-[85vw]"
            key={index}
          />
        ));
      }

      return recommendedApartments.map((apartmentProduct) => (
        <ApartmentProductCard
          key={apartmentProduct.id}
          apartmentProduct={apartmentProduct}
          className="shrink-0 grow-0 pr-3 sm:basis-1/2 lg:basis-1/3 smDown:basis-[85vw]"
          analytics={{
            linkContext:
              recommendationsAnalyticsLinkContextMap[recommendationsDistance],
            contentContext: 'omasato',
          }}
        />
      ));
    };

    return (
      <section className="w-full border-t border-black-ultralight pt-3">
        <Headline level={2} className="mb-5">
          {t(
            recommendationsDistance === 'far'
              ? 'apartmentRecommendations.headlineFar'
              : 'apartmentRecommendations.headlineNear'
          )}
        </Headline>

        <div className="sato-scrollbar-full flex overflow-x-auto pb-3">
          {renderContent()}
        </div>
      </section>
    );
  }
);
